import { useCallback, useMemo } from 'react';
import { MessageCircle } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { Button } from '../Button';
import { ChatWindow } from './ChatWindow';
import type { ChatWidgetProps } from './ChatWidget.types';
import { DEFAULT_CHAT_LABELS } from './ChatWidget.types';

export function ChatWidget({
  onSendMessage,
  messages,
  isLoading = false,
  suggestedActions = [],
  isOpen = false,
  onToggle,
  className = '',
  labels: labelsProp,
  locale = 'en',
}: ChatWidgetProps) {
  const labels = useMemo(
    () => ({ ...DEFAULT_CHAT_LABELS, ...labelsProp }),
    [labelsProp]
  );

  const handleToggle = useCallback(() => {
    onToggle?.();
  }, [onToggle]);

  const handleClose = useCallback(() => {
    if (isOpen) onToggle?.();
  }, [isOpen, onToggle]);

  return (
    <div className={className}>
      <AnimatePresence>
        {isOpen && (
          <ChatWindow
            messages={messages}
            isLoading={isLoading}
            suggestedActions={suggestedActions}
            onSendMessage={onSendMessage}
            onClose={handleClose}
            onMinimize={handleClose}
            labels={labels}
            locale={locale}
          />
        )}
      </AnimatePresence>

      <Button
        onClick={handleToggle}
        className="fixed bottom-4 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-all hover:bg-blue-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:ring-offset-gray-900 sm:bottom-6 sm:right-6"
        aria-label={labels.fabLabel}
        aria-expanded={isOpen}
        data-testid="chat-fab"
      >
        <MessageCircle className="h-6 w-6" />
      </Button>
    </div>
  );
}
